import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { useAssets, type AssetRow } from '../data/fleet.ts'
import { settingLines, usePlan, useTemplateVersions, type PlanJob } from '../data/plan.ts'
import { areasSummary, machineLabel, timeShort } from '../data/planUi.ts'
import { OPEN_WORK_ORDER_STATES, useAvailability, useChecklistVersions, useWorkOrders } from '../data/safety.ts'
import { orderLabel, prestartState, useInspections, useMyWork } from '../data/work.ts'
import { formatLocal, localDate } from '../lib/time.ts'
import { BackLink, Banner, Pill } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { Picto } from '../ui/pictos.tsx'
import { AvailabilityBanner } from './SafetyPanel.tsx'
import { SettingLinesView, frameOfAreas } from './plan/JobSettings.tsx'

const MACHINE_KINDS = ['machine', 'vehicle'] as const

/** Start working (GK-016): pick the job (today's list), then the machine, see whether its pre-start check is done, and start the timer. A FACT: it works with no signal,
 *  the timer shows at once. Nothing here decides anything the server will not decide again: a machine that is out of service on THIS device blocks the button (the device
 *  knows it), anything else (a missing check, a missing qualification, another timer) is a warning here and a flag on the server.
 *  With `?order=` (M23 item 44) the mechanic times his own work on a repair order: no job, no machine, no pre-start check. */
export function WorkStartPage() {
  const { t, i18n } = useTranslation()
  const { site, personId, kiosk } = useClub()
  const st = useEngineState()
  const search = useSearch({ strict: false }) as { job?: string; asset?: string; order?: string }
  const lang = i18n.language
  const me = kiosk?.session?.personId ?? personId
  const today = localDate(new Date(), site.timezone)
  const { ready, plan } = usePlan(lang)
  const work = useMyWork(plan.jobs)
  const access = useWriteAccess('job.execute')
  if (!ready || !work.ready) return <p>{t('common.loading')}</p>
  if (search.order) return <OrderStartForm key={search.order} orderId={search.order} running={work.mine} />
  const job = search.job ? plan.byId.get(search.job) : undefined
  if (!search.job) return <JobChooser today={today} me={me} plan={plan} asset={search.asset} allowed={access.permitted} loading={st.lastSyncAt === null && st.phase === 'syncing'} />
  if (!job) return <section className="flex flex-col gap-3"><BackLink to="/">← {t('common.today')}</BackLink><Banner tone="warn"><span data-testid="job-missing">{t('plan.jobMissing')}</span></Banner></section>
  return <StartForm key={job.id} job={job} asset={search.asset} me={me} running={work.mine} />
}

/** M23 item 25: one row per job (the title, the time, the areas summarised), my own jobs first and the others folded under them; the manual-time way at the top. */
function JobChooser({ today, me, plan, asset, allowed, loading }: { today: string; me: string | null; plan: ReturnType<typeof usePlan>['plan']; asset?: string; allowed: boolean; loading: boolean }) {
  const { t, i18n } = useTranslation()
  const areas = useAreas()
  const areaById = new Map((areas ?? []).map((a) => [a.id, a as AreaRow]))
  const open = (j: PlanJob) => j.state !== 'done' && j.state !== 'cancelled'
  const mine = (me ? plan.ofPerson(me, today) : []).map((x) => x.job).filter(open)
  const mineIds = new Set(mine.map((j) => j.id))
  const others = plan.day(today).filter((j) => open(j) && !mineIds.has(j.id))
  const row = (j: PlanJob) => (
    <li key={j.id}>
      <Link to="/work/start" search={{ job: j.id, ...(asset ? { asset } : {}) } as never} className="row-link" data-testid="choose-job" data-job={j.id}>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-lg font-semibold leading-snug">{j.title_text}{j.scheduled_start_local ? ` · ${timeShort(j.scheduled_start_local)}` : ''}</span>
          {j.areaIds.length > 0 && <span className="meta truncate" data-testid="choose-job-areas">{areasSummary(j.areaIds, areaById, i18n.language, t)}</span>}
        </span>
        <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
      </Link>
    </li>
  )
  const othersList = <ul className="flex flex-col gap-2" data-testid="choose-others-list">{others.map(row)}</ul>
  return (
    <section className="flex flex-col gap-4" data-testid="work-choose">
      <BackLink to="/">← {t('common.today')}</BackLink>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">{t('work.chooseTitle')}</h1>
        <Link to="/work/manual" className="btn w-full sm:w-auto" data-testid="manual-link"><Picto name="history" className="size-5" />{t('work.manualOpen')}</Link>
      </div>
      {!allowed && <Banner tone="warn">{t('work.notAllowed')}</Banner>}
      {mine.length > 0 && <><h2 className="text-lg font-bold">{t('work.mineToday')}</h2><ul className="flex flex-col gap-2" data-testid="choose-mine-list">{mine.map(row)}</ul></>}
      {others.length > 0 && (mine.length > 0 ? (
        <details className="flex flex-col gap-2" data-testid="choose-others">
          <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-2 text-lg font-bold [&::-webkit-details-marker]:hidden">
            <span>{t('work.othersTodayCount', { count: others.length })}</span><Picto name="chevron" className="size-5 shrink-0 rotate-90 opacity-50" />
          </summary>
          {othersList}
        </details>
      ) : <><h2 className="text-lg font-bold">{t('work.othersToday')}</h2>{othersList}</>)}
      {mine.length + others.length === 0 && <p className="meta" data-testid="choose-empty">{loading ? t('today.loadingPlan') : t('work.noneToday')}</p>}
    </section>
  )
}

function StartForm({ job, asset: assetParam, me, running }: { job: PlanJob; asset?: string; me: string | null; running: ReturnType<typeof useMyWork>['mine'] }) {
  const { t, i18n } = useTranslation()
  const { send, site } = useClub()
  const navigate = useNavigate()
  const lang = i18n.language
  const access = useWriteAccess('job.execute')
  const assets = useAssets(); const areas = useAreas(); const versions = useTemplateVersions(); const checklists = useChecklistVersions(); const inspections = useInspections()
  const { taskTypes } = usePlan(lang)
  const avail = useAvailability()
  const { busy, error, run } = useAction()
  const ids = useRef({ session: newId(), interval: newId(), allocation: newId() })
  const [picked, setPicked] = useState<string | null>(null)
  const tv = versions?.find((v) => v.id === job.template_version_id)
  const mineLine = job.crew.find((a) => a.person_id === me)
  const planned = mineLine?.asset_id ?? null
  const choice = picked ?? (assetParam ?? planned ?? '')
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  const machines = useMemo(() => (assets ?? []).filter((a) => (MACHINE_KINDS as readonly string[]).includes(a.kind) && a.lifecycle !== 'retired'), [assets])
  const wanted = new Set(tv?.default_asset_type_codes ?? [])
  const recommended = machines.filter((a) => a.id === planned || (a.asset_type_code && wanted.has(a.asset_type_code)))
  const recIds = new Set(recommended.map((a) => a.id))
  const rest = machines.filter((a) => !recIds.has(a.id))
  const asset = machines.find((a) => a.id === choice)
  const check = prestartState(asset, checklists, inspections, choice, site.timezone)
  const blocked = !!choice && avail.isBlocked(choice)
  const tt = taskTypes.find((x) => x.code === job.task_type_code)
  const closed = job.state === 'done' || job.state === 'cancelled'
  const live = running.filter((r) => r.running)
  const start = () => run(async () => {
    await send('job.start@1', { session_id: ids.current.session, interval_id: ids.current.interval, job_id: job.id, ...(choice ? { asset_id: choice, allocation_id: ids.current.allocation } : {}) })
    await navigate({ to: '/work/$sessionId', params: { sessionId: ids.current.session } })
  })
  if (!assets || !areas || !versions || !checklists || !inspections) return <p>{t('common.loading')}</p>
  return (
    <section className="flex flex-col gap-4" data-testid="work-start" data-job={job.id}>
      <BackLink to="/work/start">← {t('work.chooseTitle')}</BackLink>
      <h1 className="text-2xl font-bold" data-testid="start-title">{job.title_text}</h1>
      <div className="card flex flex-col gap-1">
        {/* review of item 19: the summary, as on the Today card and in the chooser (the full list is on the job's page) */}
        {job.areaIds.length > 0 && <p className="font-medium" data-testid="start-areas">{areasSummary(job.areaIds, areaById, lang, t)}</p>}
        {Object.keys(job.params).length > 0 && <SettingLinesView lines={settingLines(job.params, tt, lang)} frame={frameOfAreas(job.areaIds, areaById)} className="text-sm" testId="start-params" />}
        {tv?.instructions && <p className="meta">{tv.instructions[lang.startsWith('en') ? 'en' : 'fr'] ?? tv.instructions.fr}</p>}
      </div>
      {closed && <Banner tone="warn"><span data-testid="start-closed">{t('work.jobClosed')}</span></Banner>}
      {live.length > 0 && (
        <div className="flex flex-col gap-2">
          <Banner tone="warn"><span data-testid="start-already">{t('work.alreadyRunning')}</span></Banner>
          <Link to="/work/$sessionId" params={{ sessionId: live[0]!.session.id }} className="btn w-full">{t('work.openTimer')}</Link>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <label htmlFor="machine" className="text-lg font-bold">{t('work.machine')}</label>
        <select id="machine" className="input" value={choice} onChange={(e) => setPicked(e.target.value)} data-testid="start-machine">
          <option value="">{t('work.noMachine')}</option>
          {recommended.length > 0 && <optgroup label={t('work.machineSuggested')}>{recommended.map((a) => <option key={a.id} value={a.id}>{machineLabel(a)}</option>)}</optgroup>}
          {rest.length > 0 && <optgroup label={t('work.machineOthers')}>{rest.map((a) => <option key={a.id} value={a.id}>{machineLabel(a)}</option>)}</optgroup>}
        </select>
        {/* M23 item 15: the scan was a 157×17 link inside the help line; a 56-px row now */}
        <Link to="/scan" className="btn w-full" data-testid="start-scan"><Picto name="scan" className="size-5" />{t('work.scanInstead')}</Link>
        <p className="meta">{t('work.machineHelp')}</p>
      </div>
      {choice && <AvailabilityBanner assetId={choice} />}
      {choice && asset && <PrestartLine asset={asset} check={check} tz={site.timezone} job={job.id} />}
      <button className="btn btn-primary text-xl" disabled={busy || !access.allowed || closed || blocked} onClick={() => void start()} data-testid="start-go">{busy ? t('common.saving') : t('check.startWork')}</button>
      {!access.permitted && <Banner tone="warn">{t('work.notAllowed')}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}
    </section>
  )
}

/** M23 item 44: the mechanic's own time on a repair order. The same `job.start@1` with `work_order_id` instead of `job_id` (the server accepts it since M8; the
 *  projector writes the session with the order), no machine and no pre-start check: the order already names the machine, and nobody drives it to the workshop. */
function OrderStartForm({ orderId, running }: { orderId: string; running: ReturnType<typeof useMyWork>['mine'] }) {
  const { t } = useTranslation()
  const { send } = useClub()
  const navigate = useNavigate()
  const access = useWriteAccess('job.execute')
  const labour = useWriteAccess('workorder.labour')                 // cosmetic, as every UI check: the server decides again
  const orders = useWorkOrders(); const assets = useAssets()
  const { busy, error, run } = useAction()
  const ids = useRef({ session: newId(), interval: newId() })
  if (!orders || !assets) return <p>{t('common.loading')}</p>
  const order = orders.find((o) => o.id === orderId)
  if (!order) return <section className="flex flex-col gap-3"><BackLink to="/workshop">← {t('workshop.title')}</BackLink><Banner tone="warn"><span data-testid="order-missing">{t('work.orderMissing')}</span></Banner></section>
  const closed = !OPEN_WORK_ORDER_STATES.includes(order.state)
  const live = running.filter((r) => r.running)
  const start = () => run(async () => {
    await send('job.start@1', { session_id: ids.current.session, interval_id: ids.current.interval, work_order_id: order.id })
    await navigate({ to: '/work/$sessionId', params: { sessionId: ids.current.session } })
  })
  return (
    <section className="flex flex-col gap-4" data-testid="work-start" data-order={order.id}>
      <BackLink to="/assets/$assetId" params={{ assetId: order.asset_id }}>← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold" data-testid="start-title">{t('work.workshopLabour')}</h1>
      <div className="card flex flex-col gap-1">
        <p className="font-medium" data-testid="start-order">{orderLabel(order, assets, t)}</p>
        <p className="meta">{t('work.orderStartHelp')}</p>
      </div>
      {closed && <Banner tone="warn"><span data-testid="start-closed">{t('workorder.state.' + order.state)}</span></Banner>}
      {live.length > 0 && (
        <div className="flex flex-col gap-2">
          <Banner tone="warn"><span data-testid="start-already">{t('work.alreadyRunning')}</span></Banner>
          <Link to="/work/$sessionId" params={{ sessionId: live[0]!.session.id }} className="btn w-full">{t('work.openTimer')}</Link>
        </div>
      )}
      <button className="btn btn-primary text-xl" disabled={busy || !access.allowed || closed} onClick={() => void start()} data-testid="start-go">{busy ? t('common.saving') : t('check.startWork')}</button>
      {(!access.permitted || !labour.permitted) && <Banner tone="warn">{t('work.notAllowed')}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}
    </section>
  )
}

/** The pre-start check's state on the start path (M23 item 15: the way to the check was a 114×17 link inside the banner; it is a 56-px button under it now, and
 *  carries the job so the check's result page leads straight back to the start, item 14). */
export function PrestartLine({ asset, check, tz, job }: { asset: AssetRow; check: ReturnType<typeof prestartState>; tz: string; job?: string }) {
  const { t } = useTranslation()
  if (check.state === 'unknown' || check.state === 'not_required') return null
  if (check.state === 'done') return <p className="flex items-center gap-2" data-testid="prestart-done"><Pill tone="green">{t('work.checkDone')}</Pill><span className="meta">{formatLocal(check.at!, tz, 'HH:mm')}</span></p>
  return (
    <div className="flex flex-col gap-2">
      <Banner tone="warn"><span data-testid="prestart-missing">{t('work.checkMissing')}</span></Banner>
      <Link to="/assets/$assetId/check" params={{ assetId: asset.id }} search={{ asset: asset.id, ...(job ? { job } : {}) } as never} className="btn w-full" data-testid="prestart-do">{t('work.checkDo')}</Link>
    </div>
  )
}
